import React, { useMemo } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { Card, CardContent } from '../../../shared/components/ui';
import { buildCostTrendData, JOB_TYPE_COLORS } from '../../../shared/services/insightsApi';

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow-lg p-3 text-sm">
      <p className="font-semibold text-gray-900 dark:text-gray-100 mb-2">{label}</p>
      {payload.map((entry) => (
        <div key={entry.name} className="flex items-center gap-2 mb-1">
          <span
            className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
            style={{ backgroundColor: entry.color }}
          />
          <span className="text-gray-700 dark:text-gray-200">
            {entry.name}:{' '}
            <span className="font-medium">${entry.value?.toFixed(2)}/SF</span>
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * CostTrendChart — Area chart showing average cost/SF over time, grouped by job type.
 * Props: estimates (Array) — raw filtered estimates from Insights.jsx
 */
export function CostTrendChart({ estimates }) {
  const { data, jobTypes } = useMemo(() => buildCostTrendData(estimates), [estimates]);

  return (
    <Card className="shadow-sm">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Cost per SF Over Time</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Average cost/SF by job type per month</p>
          </div>
          {data.length > 0 && (
            <span className="text-xs text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded-full">
              {data.length} {data.length === 1 ? 'month' : 'months'}
            </span>
          )}
        </div>
      </div>

      <CardContent className="pt-4 pb-2">
        {data.length < 2 ? (
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <div className="w-12 h-12 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-3">
              <svg className="w-6 h-6 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                  d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
              </svg>
            </div>
            <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Not enough data for trends</p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              Add estimates with dates and cost data to see trends over time
            </p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={data} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
              <defs>
                {jobTypes.map((jt, i) => (
                  <linearGradient key={jt} id={`gradient-${i}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={JOB_TYPE_COLORS[i % JOB_TYPE_COLORS.length]} stopOpacity={0.2} />
                    <stop offset="95%" stopColor={JOB_TYPE_COLORS[i % JOB_TYPE_COLORS.length]} stopOpacity={0.02} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="month"
                tick={{ fontSize: 11, fill: '#9ca3af' }}
                tickLine={false}
                axisLine={{ stroke: '#e5e7eb' }}
              />
              <YAxis
                tickFormatter={(v) => `$${v}`}
                tick={{ fontSize: 11, fill: '#9ca3af' }}
                tickLine={false}
                axisLine={false}
                width={52}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                wrapperStyle={{ fontSize: '12px', paddingTop: '16px' }}
                iconType="circle"
                iconSize={8}
              />
              {jobTypes.map((jt, i) => (
                <Area
                  key={jt}
                  type="monotone"
                  dataKey={jt}
                  stroke={JOB_TYPE_COLORS[i % JOB_TYPE_COLORS.length]}
                  strokeWidth={2}
                  fill={`url(#gradient-${i})`}
                  connectNulls
                  dot={{ r: 3, strokeWidth: 2 }}
                  activeDot={{ r: 5 }}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}

export default CostTrendChart;
